import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Typography, Alert, Link, Divider } from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { useAuth } from '../../contexts/AuthContext';
import { logger } from '../../utils/logger';
import { useLocation } from 'react-router-dom';

const LoginForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const { login, isLoading, error } = useAuth();
  const [localError, setLocalError] = useState<string | null>(null);
  const location = useLocation();

  // Reset local error when global error changes
  useEffect(() => {
    if (error) {
      setLocalError(error);
      // Clear the email sent status if there's an error
      setEmailSent(false);
    }
  }, [error]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setLocalError('Please enter a valid email address');
      return;
    }
    
    // Clear any previous errors
    setLocalError(null);
    
    // Get source from URL query parameters
    const params = new URLSearchParams(location.search);
    const source = params.get('source');
    
    try {
      await login(email, source || undefined);
      // Only set emailSent if there's no error
      setEmailSent(true);
    } catch (err) {
      logger.error('Error in login form:', err);
      setLocalError('Failed to send magic link. Please try again later.');
      setEmailSent(false);
    }
  };

  const handleTryAgain = () => {
    setEmailSent(false);
    setLocalError(null);
  };

  const handleGoogleSignIn = () => {
    // Get source from URL query parameters
    const params = new URLSearchParams(location.search);
    const source = params.get('source');
    
    // Build the Google OAuth URL with source parameter
    const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';
    let googleAuthUrl = `${backendUrl}/api/v1/auth/google`;
    
    if (source) {
      googleAuthUrl += `?source=${encodeURIComponent(source)}`;
    }
    
    // Redirect to Google OAuth
    window.location.href = googleAuthUrl;
  };

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto' }}>
      <Typography variant="h5" component="h1" gutterBottom textAlign="center" sx={{ color: 'white' }}>
        Sign In
      </Typography>
      
      {localError && !emailSent && (
        <Alert severity="error" sx={{ mb: 2, bgcolor: 'rgba(255, 255, 255, 0.1)', color: 'white' }}>
          {localError}
          {localError.includes('token') && (
            <Box mt={1}>
              <Link
                component="button"
                variant="body2"
                onClick={handleTryAgain}
                sx={{ color: '#90caf9' }}
              >
                Click here to request a new login link
              </Link>
            </Box>
          )}
        </Alert>
      )}
      
      {emailSent && !localError ? (
        <Box>
          <Alert severity="success" sx={{ mb: 2, bgcolor: 'rgba(255, 255, 255, 0.1)', color: 'white' }}>
            Check your email! We've sent a magic link to {email}.
          </Alert>
          <Typography variant="body2" textAlign="center" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
            Click the link in your email to sign in to your account.
          </Typography>
          <Box mt={2} textAlign="center">
            <Button 
              size="small" 
              onClick={handleTryAgain}
              variant="text"
            >
              Need another link?
            </Button>
          </Box>
        </Box>
      ) : (
        <Box>
          {/* Google Sign-In Button */}
          <Button
            fullWidth
            variant="outlined"
            startIcon={<GoogleIcon />}
            onClick={handleGoogleSignIn}
            sx={{
              mb: 2,
              color: 'white',
              borderColor: 'rgba(255, 255, 255, 0.5)',
              '&:hover': {
                borderColor: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            Sign in with Google
          </Button>
          
          {/* Divider */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Divider sx={{ flex: 1, borderColor: 'rgba(255, 255, 255, 0.3)' }} />
            <Typography variant="body2" sx={{ px: 2, color: 'rgba(255, 255, 255, 0.7)' }}>
              or
            </Typography>
            <Divider sx={{ flex: 1, borderColor: 'rgba(255, 255, 255, 0.3)' }} />
          </Box>
          
          <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!localError && localError.includes('email')}
            sx={{ 
              '& label.Mui-focused': { color: 'white' }, 
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
              '& .MuiOutlinedInput-root': { 
                color: 'white',
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                '&:hover fieldset': { borderColor: 'white' },
                '&.Mui-focused fieldset': { borderColor: 'white' },
              },
            }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ 
              mt: 3, 
              mb: 2,
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.3)'
              }
            }}
            disabled={isLoading || !email.includes('@')}
          >
            {isLoading ? 'Sending...' : 'Send Magic Link'}
          </Button>
            <Typography variant="body2" textAlign="center" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              We'll email you a magic link for a password-free sign in.
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default LoginForm; 